import { Link, useTheme } from "@mui/material";
import {
  memo,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Link as RouterLink,
  useParams,
  useSearchParams,
  Navigate,
} from "react-router-dom";
import MonitorHeartIcon from "@mui/icons-material/MonitorHeart";
import WatchLaterIcon from "@mui/icons-material/WatchLater";
import { pickBy } from "lodash";
import { parse } from "tldts";

import {
  useMutation,
  useMutationState,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import PageHeader from "../PageHeader";

import Logo from "../Logo";
import NetworkFailureToast from "../NetworkFailureToast";
import { useTrueBizApi } from "../../api";
import APICompanyMatchProfile from "../../types/APICompanyMatchProfile";

import Loading from "../Loading";
import SearchLoadingBody from "../SearchLoading";
import SearchBarV2 from "../SearchBarV2";
import SearchBarV1 from "../SearchBar";
import Delayed from "../Delayed";
import useAdminSettings, {
  useAllowFraudSearch,
} from "../../utilities/adminSettings";

interface LookupDomainBodyProps {
  domain?: string;
  advanced?: {
    businessName?: string;
    email?: string;
    phone?: string;
    applicantFullName?: string;
    addressLine1?: string;
    addressLine2?: string;
    city?: string;
    stateProvince?: string;
    postalCode?: string;
    country?: string;
    externalTrackingRef?: string;
  };
  matchProfileId?: UUID;
  matchProfiles?: APICompanyMatchProfile[];
  autoRun?: boolean;
}

function LookupDomainBody({
  domain: initialDomain = "",
  advanced: initialAdvanced,
  autoRun = false,
  matchProfileId: initialMatchProfileId,
  matchProfiles,
}: LookupDomainBodyProps) {
  const api = useTrueBizApi();
  const queryClient = useQueryClient();
  const theme = useTheme();

  const [hasError, setHasError] = useState(false);
  const [reason, setReason] = useState<string | undefined>();
  const [searchMode, setSearchMode] = useState<
    "STANDARD" | "STANDARD-ADVANCED" | "DEEP" | "MCC" | "FRAUD" | null
  >(null);

  const searchBarRef = useRef<() => Promise<void>>(null);

  const renderId = useId(); // scope the mutation cache to the current page view
  const mutationKey = useMemo(() => ["submitSearch", renderId], [renderId]);

  const [settings] = useAdminSettings();
  const allowFraudSearch = useAllowFraudSearch();

  const searchMutation = useMutation({
    mutationKey,
    mutationFn: async (values: {
      mode?: "STANDARD" | "STANDARD-ADVANCED" | "DEEP" | "MCC" | "FRAUD" | null;
      location?: string | null;
      domain?: string | null;
      matchProfileId?: string;
      businessName?: string;
      description?: string;
      email?: string;
      phone?: string;
      applicantFullName?: string;
      addressLine1?: string;
      addressLine2?: string;
      city?: string;
      stateProvince?: string;
      postalCode?: string;
      country?: string;
      externalTrackingRef?: string;
    }) => {
      setHasError(false);
      setReason(undefined);
      setSearchMode(null);

      setSearchMode(values?.mode || "STANDARD");

      if (
        !values?.mode ||
        values?.mode === "STANDARD" ||
        values?.mode === "STANDARD-ADVANCED"
      ) {
        const result = await api.domainLookup({
          domain: values.domain || null,
          submitted_email: values.email || null,
          submitted_phone: values.phone || null,
          submitted_business_name: values.businessName || null,
          submitted_description: values.description || null,
          submitted_full_name: values.applicantFullName || null,
          address_line_1: values.addressLine1 || null,
          address_line_2: values.addressLine2 || null,
          city: values.city || null,
          state_province: values.stateProvince || null,
          postal_code: values.postalCode || null,
          country: values.country || null,
          external_tracking_ref: values.externalTrackingRef || null,
          match_profile_id: values.matchProfileId || null,
        });

        // TODO: populate the get cache and eventually switch the detail page over
        // to using react-query so the load is instantaneous
        return result;
      }

      if (values?.mode === "DEEP") {
        // this returns really quickly, but we want to delay a little to
        // show an animation... the animation logic needs to be refactored.
        //
        //
        // but not today.

        // we need to disambiguate if values.domain is really the domain, or the business name
        // since we're using the same form field
        const valuesDomainIsValid =
          values.domain == null ? false : parse(values.domain).domain != null;

        const result = await api.deepSearch({
          businessName: valuesDomainIsValid ? null : values.domain,
          website: valuesDomainIsValid ? values.domain : null,
          location: values?.location,
        });

        await new Promise<void>((resolve) => {
          setTimeout(() => {
            resolve();
          }, 3_250);
        });

        return result;
      }

      throw new Error(`Unhandled search mode: "${values?.mode}"`);
    },
    onError: async (e) => {
      // supposedly mutationResult.isError is supposed to be set
      // when an error is thrown, but this does not seem to work currently

      let reason: string | undefined = "";

      if (api.isResponseObjectError(e)) {
        // handle 401 as a special case
        if (e.status === 401) {
          reason = `Your search failed: unauthorized`;
        } else {
          // attempt to extract known erors
          try {
            const json = await e.json();
            const message: string | undefined = json?.message;

            if (message === "Invalid input") {
              const domainErrors = (json?.errors || []).filter(
                (err: any) => err?.field_name === "domain"
              );

              if (domainErrors.length > 0) {
                reason = domainErrors
                  .flatMap((err: any) => err?.errors || [])
                  .join(" ");
              } else {
                reason = "Search input was invalid";
              }
            }
          } catch (e) {
            // pass
          }
        }
      }

      setHasError(true);
      if (reason) {
        setReason(reason);
      }
    },
  });

  // The react devs have taken a somewhat paternalistic view that
  // side effects should preferably never, ever occur as a result of page
  // load or rendering, or if must, they need to be be idempotent.
  //
  // As a result, React StrictMode will double mount and run all useEffects
  // twice. This is OK for catching some bugs and cleanup handlers but there are
  // instances where (like auto-running a search) we really do want a side effect on
  // mount/load and we are *not* ok with it being rendered twice (even in dev), since
  // it literally costs us money. (Note that in prod builds strict mode does not
  // have this behavior.)
  //
  // The react-query docs suggest that we should be able to use things like
  // searchMutation.status, searchMutation.data etc, but because of how
  // the library interacts with react strict mode, we have to access all state
  // through this workaround ¯\_(ツ)_/¯
  const searchMutationState = useMutationState({
    filters: { mutationKey },
  });

  const mostRecentMutationState = searchMutationState.at(-1);

  const search = useCallback(
    async (values: {
      mode?: "STANDARD" | "STANDARD-ADVANCED" | "DEEP" | "MCC" | "FRAUD" | null;
      location?: string | null;
      domain?: string | null;
      matchProfileId?: string;
      businessName?: string;
      description?: string;
      email?: string;
      phone?: string;
      applicantFullName?: string;
      addressLine1?: string;
      addressLine2?: string;
      city?: string;
      stateProvince?: string;
      postalCode?: string;
      country?: string;
      externalTrackingRef?: string;
    }) => {
      // we don't want to use the isMutatingHook because it will fuck with useEffect and generally
      // be a giant pain in the ass with react strict mode
      const isAlreadySearching = queryClient.isMutating({ mutationKey }) > 0;

      if (!isAlreadySearching) {
        searchMutation.reset();
        searchMutation.mutate(values);
      }
    },
    [queryClient, searchMutation, mutationKey]
  );

  useEffect(() => {
    if (!autoRun) return;
    if (hasError) return;
    if (searchBarRef.current) {
      searchBarRef.current();
    }
  }, [autoRun, hasError, search]);

  if (mostRecentMutationState?.status === "success") {
    if (!mostRecentMutationState?.data) {
      setHasError(true);
    } else {
      const id = (mostRecentMutationState.data as any)?.tracking_id;
      const domain =
        (mostRecentMutationState.data as any)?.domain?.name || "offline";

      if (!id || !domain) {
        setHasError(true);
      } else {
        if (searchMode === "DEEP") {
          return <Navigate to={`/searches`} replace={!!autoRun} />;
        }
        return (
          <Navigate
            to={`/search/${domain}/${id}`}
            state={{ newlyCompletedSearch: true }}
            // for auto-run searches, don't allow the user to use the "back" button once completed.
            // This prevents them from kicking off another search accidentally if they navigate backwards.
            replace={!!autoRun}
          />
        );
      }
    }
  }

  if (mostRecentMutationState?.status === "pending" && !hasError) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          width: "100%",
        }}
      >
        <Delayed byMs={150}>
          <PageHeader navLinks={false} divider={false} />
          <div
            style={{
              width: "100%",
              flex: "1",
              alignItems: "center",
              justifyContent: "center",
              display: "flex",
            }}
          >
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <SearchLoadingBody searchMode={searchMode} />
            </div>
          </div>
        </Delayed>
      </div>
    );
  }

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
        width: "100vw",
        paddingLeft: "1em",
        paddingRight: "1em",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
      }}
    >
      <PageHeader
        logo={false}
        navLinks={false}
        divider={false}
        style={{ position: "absolute", top: 0 }}
      />
      {hasError && !reason && <NetworkFailureToast />}

      {!settings.allowDeepSearch ? (
        <SearchBarV1
          onSearch={(values) => search(values)}
          domain={initialDomain}
          advanced={initialAdvanced}
          matchProfileId={initialMatchProfileId}
          matchProfiles={matchProfiles}
          style={{
            paddingBottom: "15vh",
            maxWidth: "800px",
          }}
          ref={searchBarRef}
          errorReason={reason}
          onChange={() => {
            if (reason) {
              setReason(undefined);
              setHasError(false);
            }
          }}
        >
          <Logo height="55px" style={{ marginBottom: "1.5em" }} />
          <div
            style={{
              marginBottom: "1em",
              display: "flex",
              gap: "1.5em",
              justifyContent: "center",
            }}
          >
            <div
              style={{
                color: theme.palette.grey[800],
                borderBottom: `2px solid ${theme.palette.primary.main}`,
              }}
            >
              <strong>Web Presence</strong>
            </div>
            <RouterLink
              to="/mcc-discovery/search"
              style={{
                textDecoration: "none",
                color: theme.palette.primary.main,
              }}
            >
              MCC Discovery
            </RouterLink>
            {allowFraudSearch === true && (
              <RouterLink
                to="/propensity-to-fraud/search"
                style={{
                  textDecoration: "none",
                  color: theme.palette.primary.main,
                }}
              >
                Propensity to Fraud
              </RouterLink>
            )}
          </div>
        </SearchBarV1>
      ) : (
        <SearchBarV2
          onSearch={(values) => search(values)}
          domain={initialDomain}
          advanced={initialAdvanced}
          matchProfileId={initialMatchProfileId}
          matchProfiles={matchProfiles}
          style={{
            paddingBottom: "15vh",
            maxWidth: "800px",
          }}
          ref={searchBarRef}
          errorReason={reason}
          onChange={() => {
            if (reason) {
              setReason(undefined);
              setHasError(false);
            }
          }}
        >
          <Logo height="55px" style={{ marginBottom: "1.5em" }} />
        </SearchBarV2>
      )}

      <div
        style={{
          position: "absolute",
          bottom: 0,
          display: "flex",
          flexDirection: "row",
          gap: "2em",
          padding: "1em",
        }}
      >
        <Link
          component={RouterLink}
          to="/monitoring"
          sx={{
            display: "flex",
            gap: "0.25em",
            color: "#8E8E8E",
            textDecoration: "none",
          }}
        >
          <MonitorHeartIcon style={{ color: "#D3D3D3" }} />
          <span>Monitoring</span>
        </Link>
        <Link
          component={RouterLink}
          to="/searches"
          sx={{
            display: "flex",
            gap: "0.25em",
            color: "#8E8E8E",
            textDecoration: "none",
          }}
        >
          <WatchLaterIcon style={{ color: "#D3D3D3" }} />
          <span>Search History</span>
        </Link>
      </div>
    </div>
  );
}

export default function LookupDomain() {
  const api = useTrueBizApi();

  const companyMatchProfilesQuery = useQuery({
    queryKey: ["getCompanyMatchProfiles"],
    queryFn: () => api.getCompanyMatchProfiles(),
  });

  const params = useParams();
  const [searchParams] = useSearchParams();

  const domain = params.domain || searchParams.get("domain") || undefined;

  const advanced = pickBy({
    businessName: searchParams.get("businessName"),
    description: searchParams.get("description"),
    email: searchParams.get("email"),
    phone: searchParams.get("phone"),
    applicantFullName: searchParams.get("applicantFullName"),
    addressLine1: searchParams.get("addressLine1"),
    addressLine2: searchParams.get("addressLine2"),
    city: searchParams.get("city"),
    stateProvince: searchParams.get("stateProvince"),
    postalCode: searchParams.get("postalCode"),
    country: searchParams.get("country"),
    externalTrackingRef: searchParams.get("externalTrackingRef"),
  });

  if (!companyMatchProfilesQuery.data) return <Loading />;

  const liveMatchProfiles = companyMatchProfilesQuery.data.filter(
    (p) => p.status === "live"
  );

  const getMatchProfileId = () => {
    // if a query params specifies truebiz default, use that one
    if (searchParams.get("riskProfile") === "default") {
      return undefined;
    }

    let profile = null;
    // otherwise if a query params specifies a profile, use that one
    if (searchParams.get("riskProfile")) {
      profile = liveMatchProfiles.find(
        (p) => p.id === searchParams.get("riskProfile")
      );
    }

    // otherwise if there's an account default, use that one
    if (profile === null) {
      profile = liveMatchProfiles.find((p) => p.is_account_default === true);
    }

    // otherwise fall back to TrueBiz Default
    return profile?.id || undefined;
  };

  return (
    <LookupDomainBody
      matchProfiles={liveMatchProfiles}
      matchProfileId={getMatchProfileId()}
      domain={domain === "offline" ? undefined : domain}
      advanced={advanced}
      autoRun={searchParams.get("run") === "1"}
    />
  );
}

export const PrefetchLookupDomain = memo(function PrefetchLookupDomain() {
  const queryClient = useQueryClient();
  const api = useTrueBizApi();

  queryClient.prefetchQuery({
    queryKey: ["getCompanyMatchProfiles"],
    queryFn: () => api.getCompanyMatchProfiles(),
  });

  return null;
});
